import type { ReactNode } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { playerHandleSuffix } from "@bw/shared/components";
import type { PlayerRatingResponse } from "@bw/shared/types/generated";
import { MISSING, ratingLabel, tierLabel, tierSlug, UNRATED } from "./competeDisplay";

/**
 * Tier, division and the server rating (3 decimals) once placement is done.
 * `showName` adds the player's name + #id (another player's profile); the own
 * Performance view omits it.
 */
export function RatingCard({
  rating,
  showName = true,
  leading,
  bare = false,
}: {
  rating: PlayerRatingResponse;
  showName?: boolean;
  /** Replaces the large tier word, e.g. the player's avatar in a profile header. */
  leading?: ReactNode;
  /** Render without the card outline (a page header). */
  bare?: boolean;
}) {
  const hasName = rating.display_name != null && rating.display_name !== "";
  // Before placement the tier and division are only the provisional
  // default, so the player is shown as Unrated.
  const placed = rating.is_placed;
  const tierText = placed ? tierLabel(rating.tier) : UNRATED;
  const division = !placed || rating.division == null ? null : `Division ${rating.division}`;
  return (
    <Paper
      component="article"
      variant="outlined"
      aria-label="Player rating"
      data-testid="rating-card"
      sx={bare ? { p: 0, border: 0, bgcolor: "transparent" } : { p: 2, borderRadius: 3 }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        {leading ?? (
          <Typography
            data-tier={placed ? tierSlug(rating.tier) : undefined}
            sx={{ fontSize: "1.5rem", fontWeight: 800, color: placed ? "primary.main" : "text.secondary" }}
          >
            {tierText}
          </Typography>
        )}
        <Box sx={{ minWidth: 0 }}>
          {showName ? (
            <Typography sx={{ fontSize: "1.125rem", fontWeight: 700 }}>
              {hasName ? rating.display_name : MISSING}{" "}
              {hasName ? (
                <Typography component="span" variant="caption" color="text.secondary">
                  {playerHandleSuffix(rating.user_id)}
                </Typography>
              ) : null}
            </Typography>
          ) : null}
          <Typography sx={{ fontWeight: showName ? 500 : 700 }} variant={showName ? "body2" : undefined}>
            {leading ? (
              <Box
                component="span"
                data-tier={placed ? tierSlug(rating.tier) : undefined}
                sx={{ color: placed ? "primary.main" : "text.secondary", fontWeight: 700 }}
              >
                {tierText}{division || placed ? " · " : null}
              </Box>
            ) : null}
            {division}
            {division && rating.is_placed ? " · " : null}
            {rating.is_placed ? (
              <>
                rating{" "}
                <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {ratingLabel(rating.rating)}
                </Box>
              </>
            ) : null}
          </Typography>
          {rating.is_placed ? (
            <Typography variant="body2" color="text.secondary">
              Placement complete · {rating.games_played} games played
            </Typography>
          ) : (
            <Typography role="status" variant="body2" color="text.secondary">
              Placement — {rating.placement_matches} placement{" "}
              {rating.placement_matches === 1 ? "match" : "matches"} so far.{" "}
              {showName ? "The rating" : "Your rating"} settles once placement is complete.
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
